import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCredits } from "../hooks/useCredits";
import { Button, useToast, EmptyState, Input, Card, PageHeader, PageLayout } from "@dawa/ui";

interface Log {
  id: string;
  delta: number;
  description: string | null;
  created_at: string;
}

export default function Credits() {
  const { credits, add } = useCredits();
  const [logs, setLogs] = useState<Log[]>([]);
  const [promo, setPromo] = useState("");
  const { addToast, Toasts } = useToast();

  useEffect(() => {
    async function load() {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      if (!user) return;
      const { data: rows } = await supabase
        .from("credit_logs")
        .select("id, delta, description, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      setLogs(rows ?? []);
    }
    void load();
  }, [credits]);

  const handleAddCredits = async () => {
    const { bonusApplied, error } = await add(10, promo);
    if (promo) {
      if (!bonusApplied) {
        addToast(error ?? "Code promo invalide", "error");
      } else {
        addToast("Code promo appliqué", "success");
      }
    } else if (error) {
      addToast(error, "error");
    }
    setPromo("");
  };

  return (
    <PageLayout maxWidth="full">
      <PageHeader
        title="Crédits"
        subtitle="Gérez vos crédits et suivez l'historique des utilisations."
        breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Crédits" }]}
        actions={<Button onClick={() => void handleAddCredits()}>Racheter 10 crédits</Button>}
      />
      <p className="mb-4">Crédits disponibles: {credits}</p>
      <div className="mb-4 flex max-w-sm gap-2">
        <Input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Code promo" />
        <Button variant="subtle" onClick={() => void handleAddCredits()}>
          Appliquer
        </Button>
      </div>
      <Button className="mb-6" onClick={() => void handleAddCredits()}>
        Racheter 10 crédits
      </Button>
      <h2 className="mb-2 text-xl font-semibold">Historique</h2>
      {logs.length === 0 ? (
        <EmptyState
          title="Aucune utilisation enregistrée"
          description="Les opérations de crédits apparaîtront ici."
        />
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Delta</th>
                <th className="px-4 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t">
                  <td className="px-4 py-2">{new Date(log.created_at).toLocaleString()}</td>
                  <td className={`px-4 py-2 ${log.delta > 0 ? "text-success" : "text-danger"}`}>
                    {log.delta > 0 ? "+" : ""}
                    {log.delta}
                  </td>
                  <td className="px-4 py-2">{log.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Toasts />
    </PageLayout>
  );
}
